import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Scissors, RefreshCw, ChevronDown, ChevronUp, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

function TransformationForm({ pendingBatches, products, onClose, onSave }) {
  const [batchId, setBatchId] = useState("");
  const [sourceQtyUsed, setSourceQtyUsed] = useState("");
  const [outputs, setOutputs] = useState([{ product_id: "", product_name: "", quantity: "", unit: "kg", waste_qty: "0", labor_cost: "0", packaging_cost: "0" }]);
  const [saving, setSaving] = useState(false);

  const selectedBatch = pendingBatches.find(b => b.id === batchId);
  const finishedProducts = products.filter(p => p.category === "producto_final" && p.active);

  const addOutput = () => setOutputs(o => [...o, { product_id: "", product_name: "", quantity: "", unit: "kg", waste_qty: "0", labor_cost: "0", packaging_cost: "0" }]);
  const removeOutput = (idx) => setOutputs(o => o.filter((_, i) => i !== idx));
  const updateOutput = (idx, field, value) => {
    setOutputs(o => o.map((out, i) => {
      if (i !== idx) return out;
      const updated = { ...out, [field]: value };
      if (field === "product_id") {
        const p = finishedProducts.find(p => p.id === value);
        updated.product_name = p?.name || "";
        updated.unit = p?.unit || "kg";
      }
      return updated;
    }));
  };

  const totalOutput = outputs.reduce((s, o) => s + (parseFloat(o.quantity) || 0), 0);
  const totalWaste = outputs.reduce((s, o) => s + (parseFloat(o.waste_qty) || 0), 0);

  const handleSave = async () => {
    if (!batchId) { toast.error("Selecciona un lote"); return; }
    if (outputs.some(o => !o.product_id || !o.quantity)) { toast.error("Completa todos los productos y cantidades"); return; }
    const squ = parseFloat(sourceQtyUsed) || 0;
    if (squ <= 0) { toast.error("Indica los kg usados del lote"); return; }
    if (selectedBatch && squ > selectedBatch.remaining_qty) {
      toast.error(`Solo quedan ${selectedBatch.remaining_qty} kg disponibles`); return;
    }
    setSaving(true);
    try {
      await api.post("/transformations", {
        batch_id: batchId,
        source_product_id: selectedBatch?.product_id || "",
        source_product_name: selectedBatch?.product_name || "",
        source_qty_used: squ,
        outputs: outputs.map(o => ({
          ...o, quantity: parseFloat(o.quantity) || 0,
          waste_qty: parseFloat(o.waste_qty) || 0,
          labor_cost: parseFloat(o.labor_cost) || 0,
          packaging_cost: parseFloat(o.packaging_cost) || 0,
        })),
      });
      toast.success("Transformacion registrada");
      onSave();
    } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="transformation-form">
        <DialogHeader><DialogTitle>Nueva Transformacion (Corte / Empaque)</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Batch selection */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Lote de Produccion *</label>
            <select value={batchId} onChange={e => {
              setBatchId(e.target.value);
              const b = pendingBatches.find(b => b.id === e.target.value);
              if (b) setSourceQtyUsed(String(b.remaining_qty));
            }} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="trans-batch-select">
              <option value="">Seleccionar lote completado...</option>
              {pendingBatches.map(b => (
                <option key={b.id} value={b.id}>
                  {b.batch_number} - {b.product_name} | Disponible: {b.remaining_qty} kg
                </option>
              ))}
            </select>
          </div>

          {selectedBatch && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs">
              <div className="flex justify-between">
                <span className="text-blue-700"><strong>{selectedBatch.batch_number}</strong> - {selectedBatch.product_name}</span>
                <span className="text-blue-600">Costo/kg: {formatMXN(selectedBatch.cost_per_output_unit || 0)}</span>
              </div>
              <div className="flex gap-4 mt-1 text-blue-600">
                <span>Producido: {selectedBatch.actual_output_kg} kg</span>
                <span>Ya transformado: {selectedBatch.transformed_qty} kg</span>
                <span className="font-bold">Disponible: {selectedBatch.remaining_qty} kg</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Kg del lote a transformar</label>
            <input type="number" value={sourceQtyUsed} onChange={e => setSourceQtyUsed(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: 23.9" min="0" step="0.1" data-testid="trans-source-qty" />
          </div>

          {/* Outputs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Productos resultantes</span>
              <button onClick={addOutput} className="text-xs text-brand-600 hover:text-brand-700 font-medium" data-testid="add-output-btn">+ Agregar producto</button>
            </div>
            <div className="space-y-2">
              {outputs.map((out, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid={`output-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Producto</label>
                      <select value={out.product_id} onChange={e => updateOutput(idx, "product_id", e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs">
                        <option value="">Seleccionar...</option>
                        {finishedProducts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Cantidad ({out.unit})</label>
                      <input type="number" value={out.quantity} onChange={e => updateOutput(idx, "quantity", e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" placeholder="0" min="0" step="0.1" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Merma ({out.unit})</label>
                      <input type="number" value={out.waste_qty} onChange={e => updateOutput(idx, "waste_qty", e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" placeholder="0" min="0" step="0.01" />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-[10px] text-slate-500 mb-0.5">M.O. $</label>
                      <input type="number" value={out.labor_cost} onChange={e => updateOutput(idx, "labor_cost", e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" placeholder="0" min="0" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">Empaque $</label>
                      <input type="number" value={out.packaging_cost} onChange={e => updateOutput(idx, "packaging_cost", e.target.value)}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" placeholder="0" min="0" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      {outputs.length > 1 && (
                        <button onClick={() => removeOutput(idx)} className="p-1 hover:bg-red-50 rounded">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {totalOutput > 0 && (
              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                <span>Total producido: <strong>{totalOutput.toFixed(1)}</strong></span>
                <span>Total merma: <strong className="text-red-500">{totalWaste.toFixed(1)}</strong></span>
                <span>Rendimiento: <strong className="text-emerald-600">{sourceQtyUsed ? ((totalOutput / parseFloat(sourceQtyUsed)) * 100).toFixed(1) : 0}%</strong></span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-transformation-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : "Registrar Transformacion"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Transformaciones() {
  const [transformations, setTransformations] = useState([]);
  const [pendingBatches, setPendingBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [transRes, pendRes, prodRes] = await Promise.all([
        api.get("/transformations"),
        api.get("/transformations/pending-batches"),
        api.get("/products"),
      ]);
      setTransformations(transRes.data); setPendingBatches(pendRes.data); setProducts(prodRes.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteTransformation = async (id) => {
    if (!window.confirm("Eliminar transformacion?")) return;
    await api.delete(`/transformations/${id}`); toast.success("Eliminada"); load();
  };

  return (
    <div className="space-y-5" data-testid="transformaciones-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Transformaciones</h1>
          <p className="text-slate-500 text-sm mt-0.5">Corte y empaque: de lotes deshidratados a SKUs finales</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw size={14} />
          </button>
          <button onClick={() => setShowForm(true)} data-testid="new-transformation-btn"
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Scissors size={16} /> Nueva Transformacion
          </button>
        </div>
      </div>

      {/* Pending batches alert */}
      {pendingBatches.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pending-batches-alert">
          <div className="flex items-center gap-2 mb-2">
            <Package size={16} className="text-amber-600" />
            <span className="text-sm font-bold text-amber-800">{pendingBatches.length} lote(s) pendientes de transformar</span>
          </div>
          <div className="space-y-1.5">
            {pendingBatches.map(b => (
              <div key={b.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-amber-100">
                <span className="font-medium text-slate-700">{b.batch_number} - {b.product_name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-amber-600">{b.remaining_qty} kg sin transformar</span>
                  <button onClick={() => { setShowForm(true); }} className="text-brand-600 hover:text-brand-700 font-medium">
                    Transformar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transformations list */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">Cargando...</div>
        ) : transformations.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Scissors size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">Sin transformaciones registradas</p>
          </div>
        ) : transformations.map(t => (
          <div key={t.id} className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid={`trans-card-${t.id}`}>
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
              <div className="flex items-center gap-3">
                <Scissors size={16} className="text-brand-500" />
                <div>
                  <div className="font-medium text-slate-800 text-sm">{t.folio} - {t.source_product_name}</div>
                  <div className="text-[10px] text-slate-500">Lote: {t.batch_number} | Usado: {t.source_qty_used} kg | Producido: {t.total_output} | Merma: {t.total_waste}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{new Date(t.created_at).toLocaleDateString("es-MX")}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteTransformation(t.id); }} className="p-1 hover:bg-red-50 rounded">
                  <Trash2 size={14} className="text-red-400" />
                </button>
                {expanded === t.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </div>
            {expanded === t.id && (
              <div className="border-t border-slate-100 px-4 py-3">
                <table className="w-full text-xs">
                  <thead><tr className="text-slate-500">
                    <th className="text-left pb-1">Producto</th><th className="text-right pb-1">Cantidad</th>
                    <th className="text-right pb-1">Merma</th><th className="text-right pb-1">Costo MP</th>
                    <th className="text-right pb-1">M.O.</th><th className="text-right pb-1">Empaque</th>
                    <th className="text-right pb-1">Costo Total</th><th className="text-right pb-1">Costo/U</th>
                  </tr></thead>
                  <tbody>
                    {(t.outputs || []).map((o, i) => (
                      <tr key={i} className="border-t border-slate-50">
                        <td className="py-1 font-medium text-slate-700">{o.product_name}</td>
                        <td className="py-1 text-right">{o.quantity} {o.unit}</td>
                        <td className="py-1 text-right text-red-500">{o.waste_qty}</td>
                        <td className="py-1 text-right">{formatMXN(o.raw_cost_portion || 0)}</td>
                        <td className="py-1 text-right">{formatMXN(o.labor_cost || 0)}</td>
                        <td className="py-1 text-right">{formatMXN(o.packaging_cost || 0)}</td>
                        <td className="py-1 text-right font-bold">{formatMXN(o.total_cost || 0)}</td>
                        <td className="py-1 text-right text-emerald-700">{formatMXN(o.cost_per_unit || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && <TransformationForm pendingBatches={pendingBatches} products={products}
        onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
